import React,{useEffect,useLayoutEffect,useRef,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {waProjectLink} from '../data/content';

const {FiArrowUpRight}=FiIcons;

const DESKTOP_W=1440;
const DEFAULT_VIEWPORT_H=760;

export default function ProjectCard({project,index}) {
  const shotRef=useRef(null);
  const iframeRef=useRef(null);
  const cardRef=useRef(null);
  const [scale,setScale]=useState(0.4);
  const [iframeFailed,setIframeFailed]=useState(false);
  const [iframeLoaded,setIframeLoaded]=useState(false);
  const [inView,setInView]=useState(false);
  const lastWidthRef=useRef(0);

  const viewportH=project.viewportHeight || DEFAULT_VIEWPORT_H;

  // Container height=scaled visible crop (bottom-gap fix preserved).
  const containerH=viewportH * scale;

  // Responsive sizing: use useLayoutEffect so the correct scale is applied
  // BEFORE the browser paints. The lastWidthRef guard bails when only the
  // element height changed (height depends on scale), avoiding redundant
  // observer fires.
  useLayoutEffect(()=> {
    if (!project.liveUrl) return;
    const element=shotRef.current;
    if (!element) return;
    const updateSize=()=> {
      const w=element.clientWidth;
      if (w <=0 || w===lastWidthRef.current) return;
      lastWidthRef.current=w;
      setScale(w / DESKTOP_W);
    };
    updateSize();
    const resizeObserver=new ResizeObserver(updateSize);
    resizeObserver.observe(element);
    return ()=> resizeObserver.disconnect();
  },[project.liveUrl]);

  // Progressive loading: assign iframe src only when card nears viewport.
  useEffect(()=> {
    if (!project.liveUrl) return;
    const node=cardRef.current;
    if (!node) return;
    if (typeof IntersectionObserver==='undefined') {setInView(true); return;}
    const observer=new IntersectionObserver(
      (entries)=> {
        entries.forEach((entry)=> {
          if (entry.isIntersecting) {setInView(true); observer.disconnect();}
        });
      },
      {rootMargin: '300px 0px',threshold: 0.01}
    );
    observer.observe(node);
    return ()=> observer.disconnect();
  },[project.liveUrl]);

  const handleIframeLoad=()=> setIframeLoaded(true);
  const handleIframeError=()=> setIframeFailed(true);

  const projectLink=project.link || waProjectLink(project.name);
  const linkLabel=project.link ? `Visit ${project.name} website` : `Enquire about ${project.name} on WhatsApp`;
  const hasLive=Boolean(project.liveUrl);
  const showIframe=hasLive && inView && !iframeFailed;
  const showFallbackImage=!hasLive || iframeFailed;
  const showLoader=showIframe && !iframeLoaded;

  return (
    <motion.article className="proj-card" ref={cardRef}
      initial={{opacity: 0,y: 28}} whileInView={{opacity: 1,y: 0}}
      viewport={{once: true,amount: 0.15}}
      transition={{duration: 0.55,delay: index * 0.08}} >
      <div className="proj-shot" ref={shotRef}
        data-scroll-mode={project.scrollMode || 'native'}
        style={{height: `${containerH}px`}} >
        <div className="proj-shot-inner">
          {showIframe ? (
            <iframe ref={iframeRef} src={project.liveUrl}
              title={`${project.name} live website preview`}
              className="proj-shot-iframe"
              style={{width: `${DESKTOP_W}px`,height: `${viewportH}px`,
                transform: `scale(${scale})`,transformOrigin: 'top left',
                border: 0,display: 'block',background: '#fff',
                opacity: iframeLoaded ? 1 : 0,transition: 'opacity 400ms ease'}}
              scrolling="auto" tabIndex={-1}
              loading={index < 2 ? 'eager' : 'lazy'}
              onLoad={handleIframeLoad} onError={handleIframeError} />
          ) : showFallbackImage ? (
            <img src={project.image} alt={`${project.name} full-page website screenshot`} loading="lazy" />
          ) : null}
        </div>
        {showLoader && (
          <div className="proj-shot-loader" aria-hidden="true">
            <span className="proj-loader-spinner" />
            <span className="proj-loader-text">Loading preview…</span>
          </div>
        )}
        <div className="proj-shot-overlay">
          <span className="proj-shot-tag">Live Website</span>
          <a className="proj-shot-tag proj-shot-icon" href={projectLink}
            target="_blank" rel="noreferrer" aria-label={linkLabel} >
            <SafeIcon icon={FiArrowUpRight} />
          </a>
        </div>
      </div>
      <div className="proj-body">
        <div className="proj-meta">
          <span className="proj-cat">{project.category}</span>
          <span className="proj-year">{project.year}</span>
        </div>
        <h3>{project.name}</h3>
        <p>{project.desc}</p>
        <div className="proj-stack">
          {project.stack.map((technology)=> (
            <span key={technology}>{technology}</span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}
